import { useMemo } from 'react';
import { endOfMonth, format, isToday, startOfMonth } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { CalendarEvent } from '../../types/calendar';
import type { CalendarLinkSummary } from '../../services/calendarLinkSources';
import { buildEventsByDay } from '../../utils/calendarEvents';
import { getEventSpan, type EventSpan } from '../../utils/calendarLayout';
import { EmptyState } from '../ui/EmptyState';
import { spanMetaForDay } from './DayEventsPopover';
import { EventBar } from './EventBar';
import { eventColor, type TagColorLookup } from './eventColors';

export interface AgendaViewProps {
  events: readonly CalendarEvent[];
  tagColors: TagColorLookup;
  links: ReadonlyMap<string, CalendarLinkSummary>;
  currentDate: Date;
  onEventClick: (event: CalendarEvent) => void;
}

const STATUS_LABEL: Record<string, string> = { cancelled: 'Cancelled', postponed: 'Postponed' };

/** The month's events grouped by day; a multi-day event is listed under every day it covers. */
export const AgendaView = ({ events, tagColors, links, currentDate, onEventClick }: AgendaViewProps) => {
  const monthLabel = format(currentDate, 'yyyy-MM');
  const { monthStart, monthEnd } = useMemo(
    () => ({ monthStart: startOfMonth(currentDate), monthEnd: endOfMonth(currentDate) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthLabel],
  );

  const days = useMemo(() => {
    const spanCache = new Map<string, EventSpan | null>();
    const spanOf = (event: CalendarEvent) => {
      if (!spanCache.has(event.id)) spanCache.set(event.id, getEventSpan(event));
      return spanCache.get(event.id) ?? null;
    };
    return Array.from(buildEventsByDay([...events], monthStart, monthEnd).entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, dayEvents]) => ({
        key,
        date: new Date(`${key}T00:00:00`),
        spans: dayEvents.map(spanOf).filter((span): span is EventSpan => span != null),
      }));
  }, [events, monthStart, monthEnd]);

  if (days.length === 0) {
    return <EmptyState bare icon={CalendarIcon} title="No events this month" />;
  }

  return (
    <div className="space-y-4">
      {days.map(({ key, date, spans }) => {
        const today = isToday(date);
        return (
          <section key={key} aria-label={format(date, 'EEEE d MMMM')}>
            <div
              className="sticky top-0 z-10 flex items-center gap-2 mb-1.5 -mx-1 px-2 py-1 rounded-md backdrop-blur-sm"
              style={{ background: 'var(--glass-bg)' }}
            >
              <span
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold tabular-nums',
                  today ? 'bg-accent-500 text-on-solid' : 'bg-glass-200/10 text-white',
                )}
              >
                {format(date, 'd')}
              </span>
              <span className="text-sm font-semibold text-white">{format(date, 'EEEE')}</span>
              <span className="text-xs text-glass-400">{format(date, 'MMMM yyyy')}</span>
              {today && <span className="badge badge-accent ml-1">Today</span>}
            </div>
            <div className="subcard divide-y divide-glass-200/10">
              {spans.map(span => {
                const status = STATUS_LABEL[span.event.status];
                const meta = status ? `${spanMetaForDay(span, date)} · ${status}` : spanMetaForDay(span, date);
                return (
                  <div key={span.event.id} className="p-1">
                    <EventBar
                      span={span}
                      color={eventColor(span.event, tagColors)}
                      linked={links.get(span.event.id)}
                      size="md"
                      showTime={false}
                      meta={meta}
                      onClick={onEventClick}
                      className="w-full"
                    />
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};
